import { useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { BottomSheet } from "../../components/ui";
import { enviarFormularioContato } from "../../services/api/sacApi";
import "./sac.css";

const FormularioContato = () => {
  // Estados do formulário (campos em inglês para match com backend)
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    gender: "",
    subjectType: "",
    subject: "",
    transportOperator: "",
    vehicleId: "",
    line: "",
    location: "",
    occurenceDate: new Date().toISOString().split("T")[0], // Data do ocorrido (YYYY-MM-DD)
    landmark: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Estados para bottom sheets
  const [activeSheet, setActiveSheet] = useState(null);

  // Opções dos dropdowns
  const opcoes = {
    gender: ["Masculino", "Feminino", "Outro"],
    subjectType: ["Sugestão", "Denúncia", "Reclamação", "Elogio", "Informação"],
    subject: [
      "Veículo",
      "Tripulação",
      "Linhas e Horários",
      "Aplicativo",
      "Queima de parada",
      "Outro",
    ],
    line: [
      "01 Sto Eduardo - Rodoviaria",
      "01B Sto Eduardo - H.G.P.",
      "02 Batista - Tome",
      "02A Batista - H.G.P.",
      "02B Batista - H.G.P.",
      "03 Pinheirinho - Itatuba",
      "0306 Pinheirinho - Sto Antonio",
      "03A Rodoviaria - Capuava",
      "04 Rodoviaria - H.G.P.",
      "04A Rodoviaria - H.G.P.",
      "05 Rodoviaria - Caputera",
      "06 Mimas - Pinheirinho",
      "06A Rodoviaria - Sta Clara",
      "07 Rodoviaria - Angela",
      "07A H.G.P - Angela"
    ],
  };

  // Formata telefone brasileiro (XX) XXXXX-XXXX
  const formatarTelefone = (valor) => {
    // Remove tudo que não é número
    const numeros = valor.replace(/\D/g, "");

    // Limita a 11 dígitos
    const numeroLimitado = numeros.slice(0, 11);

    // Aplica máscara
    if (numeroLimitado.length <= 2) {
      return numeroLimitado;
    } else if (numeroLimitado.length <= 7) {
      return `(${numeroLimitado.slice(0, 2)}) ${numeroLimitado.slice(2)}`;
    } else {
      return `(${numeroLimitado.slice(0, 2)}) ${numeroLimitado.slice(
        2,
        7
      )}-${numeroLimitado.slice(7)}`;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Formatação especial para telefone
    if (name === "phone") {
      const valorFormatado = formatarTelefone(value);
      setFormData((prev) => ({ ...prev, [name]: valorFormatado }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Limpa erro do campo ao digitar
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const openSheet = (fieldName) => {
    setActiveSheet(fieldName);
  };

  const closeSheet = () => {
    setActiveSheet(null);
  };

  const selectOption = (option) => {
    setFormData((prev) => ({ ...prev, [activeSheet]: option }));
    if (errors[activeSheet]) {
      setErrors((prev) => ({ ...prev, [activeSheet]: "" }));
    }
    closeSheet();
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Nome é obrigatório";
    }

    if (!formData.email.trim()) {
      newErrors.email = "E-mail é obrigatório";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "E-mail inválido";
    }

    if (!formData.subjectType) {
      newErrors.subjectType = "Tipo de relato é obrigatório";
    }

    if (!formData.subject) {
      newErrors.subject = "Assunto é obrigatório";
    }

    if (!formData.occurenceDate) {
      newErrors.occurenceDate = "Data do ocorrido é obrigatória";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Mensagem é obrigatória";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Captura o token do Turnstile
    const turnstileResponse = document.querySelector(
      '[name="cf-turnstile-response"]'
    )?.value;

    if (!turnstileResponse) {
      setErrors({ submit: "Por favor, complete a verificação de segurança." });
      return;
    }

    setLoading(true);

    try {
      // Combina data escolhida + hora atual em ISO 8601
      const now = new Date();
      const [year, month, day] = formData.occurenceDate.split("-");
      const occurenceTimestamp = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        now.getHours(),
        now.getMinutes(),
        now.getSeconds()
      ).toISOString();

      // Envia formulário com token do Turnstile
      await enviarFormularioContato({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        gender: formData.gender,
        subjectType: formData.subjectType,
        subject: formData.subject,
        transportOperator: formData.transportOperator,
        vehicleId: formData.vehicleId,
        line: formData.line,
        location: formData.location,
        occurenceTimestamp: occurenceTimestamp,
        landmark: formData.landmark,
        message: formData.message,
        turnstileToken: turnstileResponse,
      });

      setSuccess(true);

      // Limpa formulário após 3 segundos
      setTimeout(() => {
        setFormData({
          name: "",
          email: "",
          phone: "",
          gender: "",
          subjectType: "",
          subject: "",
          transportOperator: "",
          vehicleId: "",
          line: "",
          location: "",
          occurenceDate: new Date().toISOString().split("T")[0],
          landmark: "",
          message: "",
        });
        setSuccess(false);

        // Reset Turnstile widget
        if (window.turnstile) {
          window.turnstile.reset();
        }
      }, 3000);
    } catch (error) {
      // Tratamento específico de erros do backend
      let errorMessage = "Erro ao enviar formulário. Tente novamente.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setErrors({ submit: errorMessage });

      // Reset Turnstile em caso de erro
      if (window.turnstile) {
        window.turnstile.reset();
      }
    } finally {
      setLoading(false);
    }
  };

  const renderSheetContent = () => {
    if (!activeSheet) return null;

    return (
      <div className="dropdown-sheet-content">
        <div className="dropdown-header">
          <h3>
            {activeSheet === "gender"
              ? "Gênero"
              : activeSheet === "subjectType"
                ? "Tipo de Relato"
                : activeSheet === "subject"
                  ? "Assunto"
                  : activeSheet === "line"
                    ? "Rotas"
                    : ""}
          </h3>
        </div>
        <div className="dropdown-options">
          {opcoes[activeSheet]?.map((option) => (
            <div
              key={option}
              className={`dropdown-option ${formData[activeSheet] === option ? "selected" : ""
                }`}
              onClick={() => selectOption(option)}
            >
              {option}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (success) {
    return (
      <div className="sac-container">
        <div className="success-message">
          <div className="success-icon">✓</div>
          <h2>Mensagem enviada com sucesso!</h2>
          <p>Sua solicitação foi registrada com sucesso.</p>
          <p>Em breve entraremos em contato.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sac-container">
      <form onSubmit={handleSubmit} className="sac-form">
        {/* Nome */}
        <div className="form-group">
          <label htmlFor="name">Nome*</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={errors.name ? "input-error" : ""}
          />
          {errors.name && <span className="error-text">{errors.name}</span>}
        </div>

        {/* E-mail */}
        <div className="form-group">
          <label htmlFor="email">E-mail*</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={errors.email ? "input-error" : ""}
            pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
            placeholder="exemplo@email.com"
          />
          {errors.email && <span className="error-text">{errors.email}</span>}
        </div>

        {/* Telefone */}
        <div className="form-group">
          <label htmlFor="phone">Telefone</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="(11) 98765-4321"
            maxLength="15"
          />
        </div>

        {/* Gênero e Tipo de Relato - lado a lado */}
        <div className="form-row">
          <div className="form-group half">
            <label>Gênero</label>
            <div className="select-input" onClick={() => openSheet("gender")}>
              <span className={!formData.gender ? "placeholder" : ""}>
                {formData.gender || "Selecione"}
              </span>
              <FaChevronDown />
            </div>
          </div>

          <div className="form-group half">
            <label>Tipo de Relato*</label>
            <div
              className={`select-input ${errors.subjectType ? "input-error" : ""
                }`}
              onClick={() => openSheet("subjectType")}
            >
              <span className={!formData.subjectType ? "placeholder" : ""}>
                {formData.subjectType || "Selecione"}
              </span>
              <FaChevronDown />
            </div>
            {errors.subjectType && (
              <span className="error-text">{errors.subjectType}</span>
            )}
          </div>
        </div>

        {/* Assunto */}
        <div className="form-group">
          <label>Assunto*</label>
          <div
            className={`select-input ${errors.subject ? "input-error" : ""}`}
            onClick={() => openSheet("subject")}
          >
            <span className={!formData.subject ? "placeholder" : ""}>
              {formData.subject || "Selecione"}
            </span>
            <FaChevronDown />
          </div>
          {errors.subject && (
            <span className="error-text">{errors.subject}</span>
          )}
        </div>

        {/* Empresa e Veículo - lado a lado */}
        <div className="form-row">
          <div className="form-group half">
            <label htmlFor="transportOperator">Empresa</label>
            <input
              type="text"
              id="transportOperator"
              name="transportOperator"
              value={formData.transportOperator}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group half">
            <label htmlFor="vehicleId">Veículo</label>
            <input
              type="text"
              id="vehicleId"
              name="vehicleId"
              value={formData.vehicleId}
              onChange={handleInputChange}
            />
          </div>
        </div>

        {/* Rotas */}
        <div className="form-group">
          <label>Rotas</label>
          <div className="select-input" onClick={() => openSheet("line")}>
            <span className={!formData.line ? "placeholder" : ""}>
              {formData.line || "-"}
            </span>
            <FaChevronDown />
          </div>
        </div>

        {/* Localização */}
        <div className="form-group">
          <label htmlFor="location">Localização</label>
          <input
            type="text"
            id="location"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
          />
        </div>

        {/* Data do Ocorrido */}
        <div className="form-group">
          <label htmlFor="occurenceDate">Data do Ocorrido*</label>
          <input
            type="date"
            id="occurenceDate"
            name="occurenceDate"
            value={formData.occurenceDate}
            onChange={handleInputChange}
            className={errors.occurenceDate ? "input-error" : ""}
            max={new Date().toISOString().split("T")[0]}
          />
          {errors.occurenceDate && (
            <span className="error-text">{errors.occurenceDate}</span>
          )}
        </div>

        {/* Ponto de referência */}
        <div className="form-group">
          <label htmlFor="landmark">Ponto de referência</label>
          <input
            type="text"
            id="landmark"
            name="landmark"
            value={formData.landmark}
            onChange={handleInputChange}
          />
        </div>

        {/* Mensagem */}
        <div className="form-group">
          <label htmlFor="message">Mensagem*</label>
          <textarea
            id="message"
            name="message"
            rows="5"
            value={formData.message}
            onChange={handleInputChange}
            className={errors.message ? "input-error" : ""}
          />
          {errors.message && (
            <span className="error-text">{errors.message}</span>
          )}
        </div>

        {/* Cloudflare Turnstile Captcha (test key) */}
        <div className="form-group">
          <div
            className="cf-turnstile"
            data-sitekey="0x4AAAAAACFBw0bocsP8qufC"
            data-theme="light"
          ></div>
        </div>

        {/* Erro geral */}
        {errors.submit && <div className="submit-error">{errors.submit}</div>}

        {/* Botão Enviar */}
        <button type="submit" className="submit-btn" disabled={loading}>
          {loading ? "Enviando..." : "Enviar"}
        </button>
      </form>

      {/* Bottom Sheet para dropdowns */}
      <BottomSheet isOpen={activeSheet !== null} onClose={closeSheet}>
        {renderSheetContent()}
      </BottomSheet>
    </div>
  );
};

export default FormularioContato;
